// @ts-nocheck
/**
 * Hidden owner-only admin-usage analytics (/admin/admin-logs2).
 * NOT in the nav registry, NOT in docs — the insights endpoint 404s for every
 * admin except the platform owner, and this page then renders a plain
 * "Not found" with no hints.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { axios } from '@store/axios';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { PageContainer, PageHeader } from '@/components/Page/PageContainer';
import { StatCardsSkeleton, TableSkeleton } from '@/components/Page/PageSkeletons';
import { RefreshCw, Users, Clock, MousePointerClick, Activity, Smartphone } from 'lucide-react';
import { toast } from 'sonner';

const baseUrl = `${process.env.API_URL}/api/v1`;

const fmtMinutes = (m) => (m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`);
const fmtWhen = (d) => new Date(d).toLocaleString(undefined, {
  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
});
const fmtDay = (d) => new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

// Organizer-nudge state (INT-0041) for the leaderboard column.
export const nudgeSummary = (n) => {
  if (!n) return 'none';
  if (n.pausedAt) return `Paused since ${fmtDay(n.pausedAt)}`;
  if (!n.sentCount) return 'none';
  return `${n.sentCount} sent, last ${n.lastTemplate}${n.lastSentAt ? ` on ${fmtDay(n.lastSentAt)}` : ''}`;
};

export default function AdminUsageAdmin() {
  const [days, setDays] = useState('30');
  const [org, setOrg] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [nudgeBusy, setNudgeBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days });
      if (org !== 'all') params.set('org', org);
      const { data: res } = await axios.get(`${baseUrl}/adminUsage/insights?${params}`);
      if (res?.status === 1) setData(res.result);
    } catch (err) {
      // This axios setup surfaces the status on err.status (no err.response).
      if ((err?.response?.status ?? err?.status) === 404) setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [days, org]);

  // Pause/resume the automated organizer nudges for one admin (INT-0041).
  // Pause when they reply or you write to them by hand; the sweep skips
  // paused organizers entirely.
  const toggleNudges = async (a) => {
    const paused = !!a.nudge?.pausedAt;
    setNudgeBusy(a.email);
    try {
      const { data: res } = await axios.post(`${baseUrl}/adminUsage/nudges/${paused ? 'resume' : 'pause'}`, { email: a.email });
      if (res?.status !== 1) throw new Error(res?.error || 'Request failed');
      toast.success(paused ? `Nudges resumed for ${a.email}` : `Nudges paused for ${a.email}`);
      await load();
    } catch (err) {
      toast.error(err?.message || 'Could not update nudges');
    } finally {
      setNudgeBusy(null);
    }
  };

  const orgName = useMemo(() => {
    const map = new Map((data?.orgs || []).map((o) => [o.id, o.name]));
    return (id) => (id ? map.get(id) || id.slice(-6) : '—');
  }, [data]);

  const totals = useMemo(() => {
    const d = data?.daily || [];
    const dev = data?.devices || {};
    const known = (dev.phone || 0) + (dev.tablet || 0) + (dev.desktop || 0);
    return {
      minutes: d.reduce((s, r) => s + r.minutes, 0),
      sessions: d.reduce((s, r) => s + r.sessions, 0),
      pageViews: d.reduce((s, r) => s + r.pageViews, 0),
      clicks: d.reduce((s, r) => s + r.clicks, 0),
      admins: Math.max(0, ...d.map((r) => r.activeAdmins ?? r.uniqueAdmins), 0),
      // INT-0042: share of recent sessions with a known device that were on a phone.
      phoneShare: known ? `${Math.round(((dev.phone || 0) / known) * 100)}%` : '—',
    };
  }, [data]);

  if (notFound) {
    return <div className="p-12 text-center text-muted-foreground">Not found</div>;
  }

  return (
    <PageContainer>
      <PageHeader
        title="Admin Usage"
        description="How admins use Staty: sessions, journeys, drop-off. Owner-only."
        actions={(
          <>
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger className="w-[130px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            <Select value={org} onValueChange={setOrg}>
              <SelectTrigger className="w-[190px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All organizations</SelectItem>
                {(data?.orgs || []).map((o) => (
                  <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={load} aria-label="Refresh">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </>
        )}
      />

      {loading && !data ? (
        <>
          <StatCardsSkeleton count={6} className="grid-cols-2 md:grid-cols-3 lg:grid-cols-6" />
          <TableSkeleton columns={7} />
        </>
      ) : (
          <>
            {/* KPI tiles */}
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
              {[
                { label: 'Peak daily admins', value: totals.admins, Icon: Users },
                { label: 'Sessions', value: totals.sessions, Icon: Activity },
                { label: 'Time in admin', value: fmtMinutes(totals.minutes), Icon: Clock },
                { label: 'Page views', value: totals.pageViews, Icon: Activity },
                { label: 'Clicks', value: totals.clicks, Icon: MousePointerClick },
                { label: 'Phone sessions', value: totals.phoneShare, Icon: Smartphone },
              ].map(({ label, value, Icon }) => (
                <Card key={label} className="shadow-sm border-0">
                  <CardContent className="pt-5 pb-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-muted-foreground">{label}</p>
                        <p className="text-xl font-bold text-foreground">{value}</p>
                      </div>
                      <Icon className="h-6 w-6 text-muted-foreground/60" aria-hidden="true" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Daily trends */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <Card className="shadow-sm border-0">
                <CardHeader>
                  <CardTitle className="text-base">Engagement per day</CardTitle>
                  <CardDescription>Active admins (beacon + auditlog history), sessions, minutes in-tab</CardDescription>
                </CardHeader>
                <CardContent className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data?.daily || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="activeAdmins" name="Admins" stroke="#6366f1" dot={false} />
                      <Line type="monotone" dataKey="sessions" name="Sessions" stroke="#10b981" dot={false} />
                      <Line type="monotone" dataKey="minutes" name="Minutes" stroke="#f59e0b" dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
              <Card className="shadow-sm border-0">
                <CardHeader>
                  <CardTitle className="text-base">Activity per day</CardTitle>
                  <CardDescription>Page views + clicks (beacon) vs API mutations (auditlogs, includes pre-beacon history)</CardDescription>
                </CardHeader>
                <CardContent className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data?.daily || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="pageViews" name="Page views" stroke="#3b82f6" dot={false} />
                      <Line type="monotone" dataKey="clicks" name="Clicks" stroke="#8b5cf6" dot={false} />
                      <Line type="monotone" dataKey="mutations" name="Mutations" stroke="#ef4444" dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            {/* Sessions */}
            <Card className="shadow-sm border-0">
              <CardHeader>
                <CardTitle className="text-base">Recent sessions</CardTitle>
                <CardDescription>Click a row to see the admin's journey (pages + clicks in order). Sessions come from the beacon, so they fill in as admins browse post-deploy.</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Admin</TableHead>
                      <TableHead>Organization</TableHead>
                      <TableHead>Device</TableHead>
                      <TableHead>Start</TableHead>
                      <TableHead>Active</TableHead>
                      <TableHead>Pages</TableHead>
                      <TableHead>Clicks</TableHead>
                      <TableHead>Exit page</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(data?.sessions || []).map((s) => {
                      const exit = s.events[s.events.length - 1];
                      return (
                        <React.Fragment key={s.sessionId}>
                          <TableRow
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => setExpanded(expanded === s.sessionId ? null : s.sessionId)}
                          >
                            <TableCell className="font-medium">{s.email || '—'}</TableCell>
                            <TableCell>{orgName(s.organizationId)}</TableCell>
                            <TableCell className="text-xs text-muted-foreground" data-testid={`session-device-${s.sessionId}`}>{s.device || 'unknown'}</TableCell>
                            <TableCell>{fmtWhen(s.start)}</TableCell>
                            <TableCell>{fmtMinutes(s.minutes)}</TableCell>
                            <TableCell>{s.pageViews}</TableCell>
                            <TableCell>{s.clicks}</TableCell>
                            <TableCell className="text-muted-foreground">{exit ? exit.p : '—'}</TableCell>
                          </TableRow>
                          {expanded === s.sessionId && (
                            <TableRow>
                              <TableCell colSpan={8} className="bg-muted/50">
                                <div className="max-h-64 overflow-y-auto py-2 space-y-1">
                                  {s.events.map((e, i) => (
                                    <div key={i} className="flex items-center gap-2 text-xs">
                                      <span className="w-32 shrink-0 text-muted-foreground/60">{fmtWhen(e.ts)}</span>
                                      {e.t === 'page_view' ? (
                                        <><Badge variant="secondary">view</Badge><span className="font-mono">{e.p}</span></>
                                      ) : (
                                        <><Badge variant="outline">click</Badge><span>{e.l}</span><span className="font-mono text-muted-foreground/60">on {e.p}</span></>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Drop-off + transitions */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <Card className="shadow-sm border-0">
                <CardHeader>
                  <CardTitle className="text-base">Where sessions end (drop-off)</CardTitle>
                  <CardDescription>Last page before the admin left</CardDescription>
                </CardHeader>
                <CardContent>
                  {(data?.exitPages || []).map((e) => {
                    const max = data.exitPages[0]?.count || 1;
                    return (
                      <div key={e.page} className="mb-2">
                        <div className="flex justify-between text-xs mb-0.5">
                          <span className="font-mono">{e.page}</span>
                          <span className="text-muted-foreground">{e.count}</span>
                        </div>
                        <div className="h-2 rounded bg-muted">
                          <div className="h-2 rounded bg-destructive/60" style={{ width: `${(e.count / max) * 100}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
              <Card className="shadow-sm border-0">
                <CardHeader>
                  <CardTitle className="text-base">Common paths</CardTitle>
                  <CardDescription>Most frequent page → page transitions</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow><TableHead>From</TableHead><TableHead>To</TableHead><TableHead className="text-right">Count</TableHead></TableRow>
                    </TableHeader>
                    <TableBody>
                      {(data?.topTransitions || []).map((t, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-mono text-xs">{t.from}</TableCell>
                          <TableCell className="font-mono text-xs">{t.to}</TableCell>
                          <TableCell className="text-right">{t.count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>

            {/* Top pages / clicks / admins */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <Card className="shadow-sm border-0">
                <CardHeader><CardTitle className="text-base">Top pages by time</CardTitle></CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow><TableHead>Page</TableHead><TableHead className="text-right">Time</TableHead><TableHead className="text-right">Views</TableHead></TableRow>
                    </TableHeader>
                    <TableBody>
                      {(data?.topPages || []).map((p) => (
                        <TableRow key={p.page}>
                          <TableCell className="font-mono text-xs">{p.page}</TableCell>
                          <TableCell className="text-right">{fmtMinutes(p.minutes)}</TableCell>
                          <TableCell className="text-right">{p.views}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
              <Card className="shadow-sm border-0">
                <CardHeader><CardTitle className="text-base">Top clicks</CardTitle></CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow><TableHead>Action</TableHead><TableHead>Page</TableHead><TableHead className="text-right">Count</TableHead></TableRow>
                    </TableHeader>
                    <TableBody>
                      {(data?.topClicks || []).map((c, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-xs">{c.label}</TableCell>
                          <TableCell className="font-mono text-xs">{c.page}</TableCell>
                          <TableCell className="text-right">{c.count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
              <Card className="shadow-sm border-0">
                <CardHeader>
                  <CardTitle className="text-base">Admins (all organizations)</CardTitle>
                  <CardDescription>Time comes from the beacon (fills in as admins browse); Actions from the full auditlog history</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Orgs</TableHead>
                        <TableHead>Device</TableHead>
                        <TableHead className="text-right">Time</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                        <TableHead className="text-right">Days</TableHead>
                        <TableHead className="text-right">Last seen</TableHead>
                        <TableHead>Nudges</TableHead>
                        <TableHead className="text-right">Nudge</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(data?.admins || []).map((a) => (
                        <TableRow key={a.email} title={(a.pages || []).join('\n')}>
                          <TableCell className="text-xs">{a.email}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {(a.orgIds || []).map(orgName).join(', ') || '—'}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground" data-testid={`admin-device-${a.email}`} title={a.deviceCounts ? `phone ${a.deviceCounts.phone} · tablet ${a.deviceCounts.tablet} · desktop ${a.deviceCounts.desktop}` : undefined}>{a.device || '—'}</TableCell>
                          <TableCell className="text-right">{a.minutes ? fmtMinutes(a.minutes) : '—'}</TableCell>
                          <TableCell className="text-right">{a.mutations || 0}</TableCell>
                          <TableCell className="text-right">{a.activeDays || 0}</TableCell>
                          <TableCell className="text-right text-xs text-muted-foreground">{fmtWhen(a.lastSeen)}</TableCell>
                          <TableCell className="text-xs text-muted-foreground" data-testid={`nudge-state-${a.email}`}>{nudgeSummary(a.nudge)}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              data-tour={a.nudge?.pausedAt ? 'usage-nudge-resume' : 'usage-nudge-pause'}
                              aria-label={`${a.nudge?.pausedAt ? 'Resume' : 'Pause'} nudges for ${a.email}`}
                              disabled={nudgeBusy === a.email}
                              onClick={() => toggleNudges(a)}
                            >
                              {a.nudge?.pausedAt ? 'Resume' : 'Pause nudges'}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          </>
      )}
    </PageContainer>
  );
}
